'use client';

import { Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Mail } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { trackLogin, trackMagicLinkRequested } from '@/lib/analytics';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.druploy.dev';

function LoginContent() {
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get('redirect_to');
  const error = searchParams.get('error');
  const prefilledEmail = searchParams.get('email') ?? '';

  const [email, setEmail] = useState(prefilledEmail);
  const [submitting, setSubmitting] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [formError, setFormError] = useState('');

  // Store redirect_to in sessionStorage so it persists through OAuth flows
  useEffect(() => {
    if (redirectTo) {
      sessionStorage.setItem('auth_redirect', redirectTo);
    }
  }, [redirectTo]);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      const resp = await fetch(`${API_URL}/api/auth/magic/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!resp.ok) throw new Error('Failed');
      trackMagicLinkRequested(email);
      setMagicSent(true);
    } catch {
      setFormError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="w-full max-w-sm">
      <CardHeader className="text-center">
        <Logo className="h-12 w-auto mx-auto" />
        <CardTitle className="text-2xl">Preview Manager</CardTitle>
        <CardDescription>Sign in to continue</CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        {error === 'not_invited' && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
            You're not allowed to sign in. Either you haven't been invited yet, or your Google/GitLab email doesn't match the email that was invited.
          </div>
        )}
        {error === 'magic_expired' && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
            This sign-in link is invalid or has expired. Please request a new one.
          </div>
        )}

        {/* GitLab OAuth temporarily hidden — redirect URI still points to the
            old preview-mr.com domain. Re-enable once the gitlab.com OAuth app
            is updated to https://api.druploy.dev/api/auth/callback/gitlab. */}
        <a
          href={`${API_URL}/api/auth/login/google`}
          onClick={() => trackLogin('google')}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md border px-4 py-2.5 text-sm font-medium hover:bg-muted transition-colors"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Continue with Google
        </a>

        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">or</span>
          </div>
        </div>

        {magicSent ? (
          <div className="rounded-md bg-green-500/10 border border-green-500/20 p-4 text-sm text-center">
            <p className="font-medium text-green-700 dark:text-green-400">Check your email</p>
            <p className="mt-1 text-muted-foreground">
              We sent a sign-in link to <strong>{email}</strong>. It expires in 15 minutes.
            </p>
          </div>
        ) : (
          <form onSubmit={handleMagicLink} className="space-y-2">
            {formError && (
              <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
                {formError}
              </div>
            )}
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
            <Button
              type="submit"
              variant="outline"
              disabled={submitting}
              className="w-full"
            >
              <Mail className="h-4 w-4 mr-2" />
              {submitting ? 'Sending...' : 'Sign in with email'}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

export default function LoginPage() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Suspense fallback={<p className="text-muted-foreground">Loading...</p>}>
        <LoginContent />
      </Suspense>
    </div>
  );
}
